import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import { UserList } from '@core/services/user';
import { UserService } from '@core/services/user.service';
import { ConsolidatedTaskService } from '@features/tasks/services/consolidated-task.service';
import { TagService } from '@features/opportunities/services/tag.service';
import { SelectListOption } from '@shared/components/form-fields/select-list-option';
import {
  TaskStatus,
  taskStatusOptions,
  TaskTracking,
  taskTrackingOptions
} from '@shared/models/tasks';

@Component({
  templateUrl: './task-consolidated.component.html',
  styleUrls: ['./task-consolidated.component.scss']
})
export class TaskConsolidatedComponent implements OnInit, OnDestroy {
  selectedTaskStatus = TaskStatus.InProgress;
  selectedTaskTracking: TaskTracking[] = [];
  form = this.fb.group({
    searchText: [],
    owned: new UntypedFormControl(false),
    tagFilter: ['']
  });
  searchText: string | null = null;
  owned: boolean = false;
  assignableUsers!: UserList;
  private refresh$ = new BehaviorSubject<null>(null);
  consolidatedTasks$ = this.refresh$.pipe(
    switchMap(() => this.consolidatedTaskService.getTasks(this.owned))
  );

  taskStatusOptions = taskStatusOptions;
  taskTrackingOptions = taskTrackingOptions;
  tagOptions: SelectListOption[] = [];
  tagFilterName: string | null = null;

  private subscription!: Subscription;
  private destroy$ = new Subject<void>();

  constructor(
    private consolidatedTaskService: ConsolidatedTaskService,
    private userService: UserService,
    private tagService: TagService,
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.subscription = this.route.queryParams.subscribe((params) => {
      if (!params.tracking) {
        return;
      }

      this.selectedTaskTracking = [params.tracking];
    });

    this.userService
      .getAssignableUsers(true, true)
      .subscribe((assignableUsers) => {
        this.assignableUsers = assignableUsers;
      });

    this.loadTagOptions();
    this.setupTagFilterListener();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onChoiceChange(event: string[]): void {
    this.selectedTaskStatus = event[0] as TaskStatus;
  }

  public onTrackingChoiceChange(event: string[]): void {
    this.selectedTaskTracking = [...event] as TaskTracking[];
  }

  public onToggle(value: boolean): void {
    this.owned = value;
    this.refresh$.next(null);
  }

  get isDefaultFilter() {
    return (
      this.isInProgressView &&
      !this.form.value.searchText &&
      !this.selectedTaskTracking.length &&
      !this.tagFilterName
    );
  }

  get isInProgressView() {
    return this.selectedTaskStatus === TaskStatus.InProgress;
  }

  private loadTagOptions(): void {
    this.tagService.getAllTags()
      .pipe(takeUntil(this.destroy$))
      .subscribe(tags => {
        this.tagOptions = [
          { label: 'All Tags', value: '' },
          ...tags.map(tag => ({ label: tag.tagName, value: tag.tagId }))
        ];
      });
  }

  private setupTagFilterListener(): void {
    this.form.get('tagFilter')!.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedTagId: string) => {
        if (!selectedTagId) {
          this.tagFilterName = null;
          return;
        }
        const selectedOption = this.tagOptions.find(o => o.value === selectedTagId);
        this.tagFilterName = selectedOption?.label ?? null;
      });
  }
}
